import React from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Button,
  CircularProgress,
  LinearProgress,
  Chip,
  Avatar,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@material-ui/core';
import { 
  Error as ErrorIcon, 
  Warning as WarningIcon, 
  Info as InfoIcon,
  CheckCircle,
  Refresh,
  ExpandMore,
  ArrowBack,
  Launch,
  Timeline,
  Schedule,
  Warning,
  CallSplit as BranchIcon,
  TrendingUp,
  Assessment
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { Link } from "@backstage/core-components";
import { 
  TabPanelProps,
  ProjectBranchNamingReport,
} from './types';
import { 
  getReleaseStatusColor,  
  getComplianceColor, 
  getDeploymentStatusColor,
} from './utils';

// ============================================================================
// STATUS ICON COMPONENTS
// ============================================================================

export const getHealthStatusIcon = (status: string) => {
  switch (status) {
    case 'healthy':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 18 }} />;
    case 'warning':
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
    case 'stale':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    default:
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
  }
};

export const getStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 18 }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    case 'running':
      return <Timeline style={{ color: '#2196f3', fontSize: 18 }} />;
    default:
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
  }
};

export const getReleaseStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 18 }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    case 'inProgress':
      return <Timeline style={{ color: '#2196f3', fontSize: 18 }} />;
    case 'canceled':
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
    case 'active':
      return <CheckCircle style={{ color: '#2196f3', fontSize: 18 }} />;
    case 'draft':
      return <Schedule style={{ color: '#9e9e9e', fontSize: 18 }} />;
    default:
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
  }
};

export const getEnvironmentStatusIcon = (status: string) => {
  switch (status) {
    case 'succeeded':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 16 }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 16 }} />;
    case 'inProgress':
      return <Timeline style={{ color: '#2196f3', fontSize: 16 }} />;
    case 'canceled':
      return <Warning style={{ color: '#ff9800', fontSize: 16 }} />;
    case 'queued':
      return <Schedule style={{ color: '#607d8b', fontSize: 16 }} />;
    case 'notStarted':
      return <Schedule style={{ color: '#9e9e9e', fontSize: 16 }} />;
    default:
      return <Warning style={{ color: '#ff9800', fontSize: 16 }} />;
  }
};

export const getComplianceIcon = (status: string) => {
  switch (status) {
    case 'compliant':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 18 }} />;
    case 'non_compliant':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    case 'partial':
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
    case 'unknown':
      return <Warning style={{ color: '#9e9e9e', fontSize: 18 }} />;
    default:
      return <Warning style={{ color: '#9e9e9e', fontSize: 18 }} />;
  }
};

export const getDeploymentStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle style={{ color: '#4caf50', fontSize: 18 }} />;
    case 'failed':
      return <ErrorIcon style={{ color: '#f44336', fontSize: 18 }} />;
    case 'in_progress':
      return <Timeline style={{ color: '#2196f3', fontSize: 18 }} />;
    case 'cancelled':
      return <Warning style={{ color: '#ff9800', fontSize: 18 }} />;
    case 'on_hold':
      return <Schedule style={{ color: '#9c27b0', fontSize: 18 }} />;
    case 'planned':
      return <Schedule style={{ color: '#607d8b', fontSize: 18 }} />;
    default:
      return <Warning style={{ color: '#9e9e9e', fontSize: 18 }} />;
  }
};

// ============================================================================
// STATUS CHIP COMPONENTS (Moved from utils.ts)
// ============================================================================

export const getHealthStatusChip = (status: string) => {
  const chipProps = {
    healthy: { label: 'Healthy', style: { backgroundColor: '#4caf50', color: 'white' } },
    warning: { label: 'Warning', style: { backgroundColor: '#ff9800', color: 'white' } },
    stale: { label: 'Stale', style: { backgroundColor: '#f44336', color: 'white' } }
  };
  
  const props = chipProps[status as keyof typeof chipProps] || chipProps.warning;
  return <Chip size="small" {...props} />;
};

export const getStatusChip = (status: string) => {
  const colors = {
    succeeded: { bg: '#4caf50', color: 'white' },
    failed: { bg: '#f44336', color: 'white' },
    running: { bg: '#2196f3', color: 'white' },
    disabled: { bg: '#9e9e9e', color: 'white' }
  };
  const color = colors[status as keyof typeof colors] || colors.disabled;
  return <Chip size="small" label={status} style={{ backgroundColor: color.bg, color: color.color }} />;
};

export const getReleaseStatusChip = (status: string) => {
  const color = getReleaseStatusColor(status);
  return <Chip size="small" label={status} style={{ backgroundColor: color, color: 'white' }} />;
};

export const getComplianceChip = (status: string) => {
  const color = getComplianceColor(status);
  const labelMap: Record<string, string> = {
    compliant: 'Compliant',
    non_compliant: 'Non-Compliant',
    partial: 'Partial',
    unknown: 'Unknown'
  };
  const label = labelMap[status] || status;
  return <Chip size="small" label={label} style={{ backgroundColor: color, color: 'white' }} />;
};

export const getDeploymentStatusChip = (status: string) => {
  const color = getDeploymentStatusColor(status);
  const labelMap: Record<string, string> = {
    completed: 'Completed',
    failed: 'Failed',
    in_progress: 'In Progress',
    cancelled: 'Cancelled',
    on_hold: 'On Hold',
    planned: 'Planned'
  };
  const label = labelMap[status] || status;
  return <Chip size="small" label={label} style={{ backgroundColor: color, color: 'white' }} />;
};

// ============================================================================
// BRANCH NAMING SUMMARY CARDS (Updated to use correct types)
// ============================================================================

interface BranchNamingSummaryCardsProps {
  report: ProjectBranchNamingReport;
}

export const BranchNamingSummaryCards: React.FC<BranchNamingSummaryCardsProps> = ({ report }) => {
  return (
    <Grid container spacing={3} style={{ marginBottom: '24px' }}>
      {/* Total Branches */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2" color="textSecondary">Total Branches</Typography>
              <BranchIcon style={{ color: '#2196f3', fontSize: 20 }} />
            </div>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196f3', marginBottom: '4px' }}>
              {report.totalBranches.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Across {report.totalRepositories} repositories
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Compliance Rate */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2" color="textSecondary">Compliance Rate</Typography>
              <TrendingUp style={{ color: '#4caf50', fontSize: 20 }} />
            </div>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '4px' }}>
              {report.overallComplianceRate.toFixed(1)}%
            </Typography>
            <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: '4px', height: '6px', marginTop: '8px' }}>
              <div 
                style={{ 
                  width: `${report.overallComplianceRate}%`, 
                  backgroundColor: '#4caf50', 
                  borderRadius: '4px', 
                  height: '100%' 
                }} 
              />
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Violations */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2" color="textSecondary">Violations</Typography>
              <ErrorIcon style={{ color: '#f44336', fontSize: 20 }} />
            </div>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#f44336', marginBottom: '4px' }}>
              {report.violatingBranches.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {report.nonCompliantRepositories} repositories affected
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Compliant Branches */}
      <Grid item xs={12} sm={6} md={3}>
        <Card elevation={2}>
          <CardContent style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography variant="body2" color="textSecondary">Compliant</Typography>
              <CheckCircle style={{ color: '#4caf50', fontSize: 20 }} />
            </div>
            <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4caf50', marginBottom: '4px' }}>
              {(report.totalBranches - report.violatingBranches).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Following naming policy
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// BRANCH NAMING CHART (Updated to use correct types)
// ============================================================================

interface BranchNamingChartProps {
  report: ProjectBranchNamingReport;
}

export const BranchNamingChart: React.FC<BranchNamingChartProps> = ({ report }) => {
  const compliantBranches = report.totalBranches - report.violatingBranches;
  
  const chartData = [
    {
      name: 'Compliant',
      value: compliantBranches,
      color: '#4caf50'
    },
    {
      name: 'Violations',
      value: report.violatingBranches,
      color: '#f44336'
    }
  ];

  const violationTypeData = report.summary.violationsByType.map(violation => ({
    type: violation.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    count: violation.count,
    description: violation.description
  }));

  return (
    <Grid container spacing={3} style={{ marginBottom: '24px' }}>
      {/* Overall Compliance Chart */}
      <Grid item xs={12} lg={6}>
        <Card elevation={2}>
          <CardContent>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <TrendingUp style={{ color: '#2196f3', fontSize: 24 }} />
              <Typography variant="h6">Overall Compliance</Typography>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {chartData.map((item) => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div 
                    style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: item.color
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.value} branches
                      </Typography>
                    </div>
                    <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          width: `${(item.value / report.totalBranches) * 100}%`, 
                          backgroundColor: item.color, 
                          borderRadius: '4px', 
                          height: '100%' 
                        }} 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Violation Types */}
      <Grid item xs={12} lg={6}>
        <Card elevation={2}>
          <CardContent>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <Assessment style={{ color: '#2196f3', fontSize: 24 }} />
              <Typography variant="h6">Violation Types</Typography>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {violationTypeData.map((violation) => {
                const violationIcon = violation.type.includes('Uppercase') ? '🔤' : 
                                    violation.type.includes('Special') ? '🔗' : 
                                    violation.type.includes('Length') ? '📏' : 
                                    violation.type.includes('Separator') ? '➖' : '⚠️';
                
                return (
                  <div key={violation.type} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    padding: '12px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '8px' 
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>{violationIcon}</span>
                      <div>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {violation.type}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {violation.description}
                        </Typography>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {violation.count}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        violations
                      </Typography>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ============================================================================
// TAB PANEL COMPONENT
// ============================================================================

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

// ============================================================================
// LOADING COMPONENTS
// ============================================================================

interface LoadingComponentProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'circular' | 'linear';
  progress?: number;
}

export const LoadingComponent: React.FC<LoadingComponentProps> = ({ 
  message = "Loading...", 
  size = 'medium',
  variant = 'circular',
  progress
}) => {
  const sizeMap = {
    small: 24,
    medium: 40,
    large: 60
  };

  return (
    <div style={{ 
      padding: '20px', 
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      {variant === 'circular' ? (
        <CircularProgress 
          size={sizeMap[size]} 
          {...(progress !== undefined && { variant: 'determinate', value: progress })}
        />
      ) : (
        <div style={{ width: '100%', maxWidth: '300px' }}>
          <LinearProgress 
            {...(progress !== undefined && { variant: 'determinate', value: progress })}
          />
        </div>
      )}
      <Typography variant="body1" color="textSecondary">
        {message}
      </Typography>
      {progress !== undefined && (
        <Typography variant="caption" color="textSecondary">
          {Math.round(progress)}%
        </Typography>
      )}
    </div>
  );
};

interface LoadingOverlayProps {
  isLoading: boolean;
  message?: string;
  children: React.ReactNode;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  isLoading,
  message = "Loading...",
  children
}) => (
  <div style={{ position: 'relative', minHeight: '200px' }}>
    {children}
    {isLoading && (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <LoadingComponent message={message} />
      </div>
    )}
  </div>
);

// ============================================================================
// ERROR COMPONENTS
// ============================================================================

interface ErrorComponentProps {
  message: string;
  title?: string;
  severity?: 'error' | 'warning' | 'info';
  actionLabel?: string;
  onAction?: () => void;
  showIcon?: boolean;
}

export const ErrorComponent: React.FC<ErrorComponentProps> = ({ 
  message, 
  title = "Error",
  severity = 'error',
  actionLabel,
  onAction,
  showIcon = true
}) => {
  const getIcon = () => {
    switch (severity) {
      case 'error':
        return <ErrorIcon style={{ fontSize: 60, color: '#f44336' }} />;
      case 'warning':
        return <WarningIcon style={{ fontSize: 60, color: '#ff9800' }} />;
      case 'info':
        return <InfoIcon style={{ fontSize: 60, color: '#2196f3' }} />;
      default:
        return <ErrorIcon style={{ fontSize: 60, color: '#f44336' }} />;
    }
  };

  const getColor = () => {
    switch (severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      default:
        return '#f44336';
    }
  };

  return (
    <div style={{ 
      padding: '40px', 
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      {showIcon && getIcon()}
      <Typography variant="h6" style={{ color: getColor(), marginBottom: '8px' }}>
        {title}
      </Typography>
      <Typography variant="body1" color="textSecondary" style={{ maxWidth: '500px' }}>
        {message}
      </Typography>
      {actionLabel && onAction && (
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={onAction}
          startIcon={<Refresh />}
          style={{ marginTop: '16px' }}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

interface AlertComponentProps {
  severity: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  onClose?: () => void;
  style?: React.CSSProperties;
}

export const AlertComponent: React.FC<AlertComponentProps> = ({
  severity,
  title,
  message,
  action,
  onClose,
  style
}) => (
  <Alert 
    severity={severity} 
    style={{ marginBottom: '20px', ...style }}
    {...(onClose && { onClose })}
    {...(action && {
      action: (
        <Button color="inherit" size="small" onClick={action.onClick}>
          {action.label}
        </Button>
      )
    })}
  >
    {title && <strong>{title}: </strong>}
    {message}
  </Alert>
);

// ============================================================================
// EMPTY STATE COMPONENTS
// ============================================================================

interface EmptyStateComponentProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

export const EmptyStateComponent: React.FC<EmptyStateComponentProps> = ({ 
  title, 
  description, 
  icon,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction
}) => (
  <Card elevation={1}>
    <CardContent style={{ 
      textAlign: 'center', 
      padding: '60px 40px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      {icon && <div style={{ marginBottom: '16px' }}>{icon}</div>}
      <Typography variant="h6" style={{ marginBottom: '8px', color: '#666' }}>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ maxWidth: '400px', marginBottom: '24px' }}>
        {description}
      </Typography>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {actionLabel && onAction && (
          <Button variant="contained" color="primary" onClick={onAction}>
            {actionLabel}
          </Button>
        )}
        {secondaryActionLabel && onSecondaryAction && (
          <Button variant="outlined" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

// ============================================================================
// NAVIGATION COMPONENTS
// ============================================================================

interface BackButtonProps {
  label?: string;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
  style?: React.CSSProperties;
}

export const BackButton: React.FC<BackButtonProps> = ({
  label = "Back",
  onClick,
  variant = 'outlined',
  style
}) => (
  <Button 
    variant={variant}
    onClick={onClick}
    startIcon={<ArrowBack />}
    style={{ marginRight: '10px', ...style }}
  >
    {label}
  </Button>
);

interface BreadcrumbsProps {
  items: Array<{
    label: string;
    onClick?: () => void;
    active?: boolean;
  }>;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    gap: '8px', 
    marginBottom: '20px',
    flexWrap: 'wrap'
  }}>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && (
          <Typography variant="body2" color="textSecondary">
            /
          </Typography>
        )}
        {item.onClick && !item.active ? (
          <Button 
            size="small" 
            style={{ textTransform: 'none', padding: '4px 8px', minWidth: 'auto' }}
            onClick={item.onClick}
          >
            {item.label}
          </Button>
        ) : (
          <Typography 
            variant="body2" 
            style={{ 
              fontWeight: item.active ? 'bold' : 'normal',
              color: item.active ? '#1976d2' : '#666'
            }}
          >
            {item.label}
          </Typography>
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============================================================================
// STATUS AND METRIC COMPONENTS
// ============================================================================

interface StatusChipProps {
  status: string;
  color?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'outlined';
}

export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  color = '#2196f3',
  icon,
  variant = 'default'
}) => (
  <Chip 
    size="small" 
    label={status}
    icon={React.isValidElement(icon) ? icon : undefined}
    variant={variant}
    style={{ 
      backgroundColor: variant === 'default' ? color : 'transparent',
      color: variant === 'default' ? 'white' : color,
      borderColor: variant === 'outlined' ? color : undefined
    }}
  />
);

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  onClick?: () => void;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = '#2196f3',
  trend,
  onClick
}) => (
  <Card 
    elevation={1}
    style={{ 
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s ease',
      height: '100%'
    }}
    onClick={onClick}
  >
    <CardContent style={{ textAlign: 'center', padding: '20px' }}>
      {icon && (
        <div style={{ marginBottom: '8px' }}>
          {React.cloneElement(icon as React.ReactElement, {
            style: { fontSize: 40, color, marginBottom: '8px' }
          })}
        </div>
      )}
      <Typography variant="h4" style={{ fontWeight: 'bold', color, marginBottom: '4px' }}>
        {value}
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: '4px' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="textSecondary">
          {subtitle}
        </Typography>
      )}
      {trend && (
        <div style={{ marginTop: '8px' }}>
          <Typography 
            variant="caption" 
            style={{ 
              color: trend.isPositive ? '#4caf50' : '#f44336',
              fontWeight: 'bold'
            }}
          >
            {trend.isPositive ? '↗' : '↘'} {Math.abs(trend.value)}%
          </Typography>
        </div>
      )}
    </CardContent>
  </Card>
);

// ============================================================================
// USER DISPLAY COMPONENTS
// ============================================================================

interface UserAvatarProps {
  name: string;
  email?: string;
  imageUrl?: string;
  size?: 'small' | 'medium' | 'large';
  showDetails?: boolean;
  onClick?: () => void;
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
  name,
  email,
  imageUrl,
  size = 'medium',
  showDetails = true,
  onClick
}) => {
  const sizeMap = {
    small: 24,
    medium: 32,
    large: 48
  };

  const avatarSize = sizeMap[size];

  return (
    <div 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px',
        cursor: onClick ? 'pointer' : 'default'
      }}
      onClick={onClick}
    >
      <Avatar 
        src={imageUrl} 
        style={{ width: avatarSize, height: avatarSize }}
      >
        {name.charAt(0).toUpperCase()}
      </Avatar>
      {showDetails && (
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            {name}
          </Typography>
          {email && (
            <Typography variant="caption" color="textSecondary" style={{ lineHeight: 1.2 }}>
              {email}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// DATA DISPLAY COMPONENTS
// ============================================================================

interface InfoRowProps {
  label: string;
  value: string | React.ReactNode;
  valueColor?: string;
  bold?: boolean;
}

export const InfoRow: React.FC<InfoRowProps> = ({
  label,
  value,
  valueColor,
  bold = false
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
    <Typography variant="body2" color="textSecondary">
      {label}:
    </Typography>
    <Typography 
      variant="body2" 
      style={{ 
        fontWeight: bold ? 'bold' : 'normal',
        color: valueColor || 'inherit'
      }}
    >
      {value}
    </Typography>
  </div>
);

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  badge?: string | number;
  icon?: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultExpanded = false,
  badge,
  icon
}) => (
  <Accordion defaultExpanded={defaultExpanded}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        {icon}
        <Typography variant="h6" style={{ flex: 1 }}>
          {title}
        </Typography>
        {badge && (
          <Chip 
            size="small" 
            label={badge}
            style={{ marginRight: '16px' }}
          />
        )}
      </div>
    </AccordionSummary>
    <AccordionDetails>
      {children}
    </AccordionDetails>
  </Accordion>
);

// ============================================================================
// LINK COMPONENTS
// ============================================================================

interface ExternalLinkProps {
  href: string;
  children: React.ReactNode;
  showIcon?: boolean;
  color?: 'primary' | 'secondary' | 'inherit';
  onClick?: (e: React.MouseEvent) => void;
}

export const ExternalLink: React.FC<ExternalLinkProps> = ({
  href,
  children,
  showIcon = true,
  color = 'primary',
  onClick
}) => (
  <Link 
    to={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    style={{ 
      color: color === 'primary' ? '#1976d2' : color === 'secondary' ? '#dc004e' : 'inherit',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }}
  >
    {children}
    {showIcon && <Launch style={{ fontSize: 16 }} />}
  </Link>
);

// ============================================================================
// PROGRESS COMPONENTS
// ============================================================================

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  color?: string;
  height?: number;
  showPercentage?: boolean;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  color = '#2196f3',
  height = 8,
  showPercentage = true
}) => {
  const percentage = Math.round((value / max) * 100);

  return (
    <div style={{ width: '100%' }}>
      {(label || showPercentage) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          {label && (
            <Typography variant="caption" color="textSecondary">
              {label}
            </Typography>
          )}
          {showPercentage && (
            <Typography variant="caption" color="textSecondary">
              {percentage}%
            </Typography>
          )}
        </div>
      )}
      <div style={{
        width: '100%',
        height: `${height}px`,
        backgroundColor: '#f0f0f0',
        borderRadius: `${height / 2}px`,
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          backgroundColor: color,
          borderRadius: `${height / 2}px`,
          transition: 'width 0.3s ease'
        }} />
      </div>
    </div>
  );
};

// ============================================================================
// SECTION DIVIDER COMPONENT
// ============================================================================

interface SectionDividerProps {
  title?: string;
  subtitle?: string;
  spacing?: 'small' | 'medium' | 'large';
}

export const SectionDivider: React.FC<SectionDividerProps> = ({
  title,
  subtitle,
  spacing = 'medium'
}) => {
  const spacingMap = {
    small: '16px',
    medium: '24px',
    large: '32px'
  };

  const marginValue = spacingMap[spacing];

  return (
    <div style={{ margin: `${marginValue} 0` }}>
      {(title || subtitle) && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          {title && (
            <Typography variant="h6" style={{ marginBottom: '4px' }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </div>
      )}
      <Divider />
    </div>
  );
};